import type { ClassData, Assessment, Grade, ReleaseCondition } from '../types';
import { evaluateCondition } from './conditionalRelease';

/**
 * releaseEligibility — may a STUDENT-READABLE copy of this mark exist right now?
 *
 * ── THE DISTINCTION THIS MODULE EXISTS TO ENFORCE ───────────────────────────────────────────────
 *
 * There are two kinds of grade record and they must not be confused:
 *
 *   AUTHORITATIVE TEACHER RECORDS — `classData.grades` (the live working marks),
 *   `classData.releasedGrades` (the teacher's released snapshot), `classData.assessments`
 *   (content, rubrics, approvals) and every `feedback` string in them. These are the teacher's
 *   work. **Nothing in this module ever alters them.** No regrading, no relabelling, no deletion.
 *
 *   STUDENT-READABLE COPIES — documents in `classes/{id}/releasedGrades/{studentId}-{assessmentId}`,
 *   which a student reads directly via `readerUids`. These are a *mirror*, created for access, and
 *   they are the only thing this module governs. When a mark should no longer be visible, the
 *   COPY is removed. The teacher's record is untouched, so nothing is lost and the mark reappears
 *   the moment it is eligible again.
 *
 * ── WHY ──────────────────────────────────────────────────────────────────────────────────────────
 *
 * `releaseGrades` mirrored every released mark except those linked to an unreleased test. It did
 * not check `isDraft`, `isHidden`, or per-assessment release conditions, and its purge had the same
 * gap. So a teacher who released the term's finished work while a draft assessment carried
 * provisional marks published those marks **and their feedback** to a document the student could
 * read — and an assessment re-restricted after a release left its copy readable. The portal never
 * rendered either, because it iterates the server-filtered assessment list, which meant the UI was
 * the only thing honouring the restriction.
 *
 * The predicate below is deliberately the same shape as the one `buildStudentClassView` applies
 * when deciding which assessments a student may see, and it calls the same `evaluateCondition`.
 * Two places deciding "may this student see this" separately is how they drift apart.
 */

/** Why a mark must not have a student-readable copy, or null when it may. */
export type RestrictionReason =
  | 'draft'                 // teacher has not published the assessment
  | 'hidden'                // teacher has hidden it from students
  | 'unreleased-test'       // linked to a test whose results are not released
  | 'release-condition';    // a per-assessment condition this student has not met

export interface ClassRestrictionContext {
  assessmentsById: Map<string, Assessment>;
  /** Assessment ids linked to a test whose results are not released. */
  unreleasedTestAssessmentIds: Set<string>;
  /** The class data `evaluateCondition` needs; conditions read live grades/submissions/progress. */
  conditionContext: ClassData;
}

/**
 * Build the per-class context once, rather than per grade. A class can hold thousands of marks and
 * `evaluateCondition` is called per (student, assessment).
 */
export const buildRestrictionContext = (classData: ClassData): ClassRestrictionContext => {
  const assessmentsById = new Map<string, Assessment>();
  for (const a of classData.assessments || []) if (a?.id) assessmentsById.set(a.id, a);

  const unreleasedTestAssessmentIds = new Set<string>();
  for (const qz of (classData.popQuizzes || [])) {
    if (qz?.kind === 'test' && !qz.resultsReleased) {
      for (const aid of (qz.assessmentIds || [])) if (aid) unreleasedTestAssessmentIds.add(aid);
    }
  }
  return { assessmentsById, unreleasedTestAssessmentIds, conditionContext: classData };
};

/**
 * May a student-readable copy of this student's mark for this assessment exist?
 *
 * A mark whose assessment has been DELETED is restricted: there is nothing for the student to see
 * it against, and leaving an orphaned readable copy is the same failure in a different shape.
 */
export const restrictionFor = (
  ctx: ClassRestrictionContext,
  studentId: string,
  assessmentId: string,
): RestrictionReason | null => {
  const a = ctx.assessmentsById.get(assessmentId);
  // No assessment -> no legitimate reason for a readable copy. Treated as draft (the most
  // conservative of the reasons) rather than inventing a fifth.
  if (!a) return 'draft';
  if (a.isDraft === true) return 'draft';
  if (a.isHidden === true) return 'hidden';
  if (ctx.unreleasedTestAssessmentIds.has(assessmentId)) return 'unreleased-test';
  const cond = (a as Assessment & { releaseCondition?: ReleaseCondition }).releaseCondition;
  if (cond && !evaluateCondition(cond, { studentId, classData: ctx.conditionContext })) {
    return 'release-condition';
  }
  return null;
};

export interface SplitResult {
  /** Grade keys whose student-readable copy may exist. */
  readable: string[];
  /** Grade keys whose student-readable copy must NOT exist — write never, delete if present. */
  restricted: Array<{ key: string; reason: RestrictionReason }>;
}

/**
 * Split a released-grade map into the copies a student may read and the copies that must be
 * removed. The input is the TEACHER's released snapshot and is never modified.
 *
 * Both halves are returned because both are needed: `readable` decides what to write, and
 * `restricted` decides what to DELETE — which is the half that handles "previously released, now
 * restricted again". Returning only the first would fix new writes and leave old copies behind.
 */
export const splitReleasedGrades = (
  classData: ClassData,
  releasedGrades: Record<string, Grade | undefined>,
): SplitResult => {
  const ctx = buildRestrictionContext(classData);
  const readable: string[] = [];
  const restricted: Array<{ key: string; reason: RestrictionReason }> = [];
  for (const [key, grade] of Object.entries(releasedGrades || {})) {
    if (!grade) continue;
    const studentId = String(grade.studentId ?? '');
    const assessmentId = String(grade.assessmentId ?? '');
    const reason = restrictionFor(ctx, studentId, assessmentId);
    if (reason) restricted.push({ key, reason });
    else readable.push(key);
  }
  return { readable, restricted };
};

/**
 * Every grade key whose student-readable copy must be removed — the restricted half of the
 * released snapshot PLUS any key that is in the live grades but not released at all.
 *
 * The second half matters because a copy can outlive its release: a mark released, then withdrawn
 * from the released snapshot, would otherwise keep its readable document. Computed from the
 * teacher's own maps, so it never guesses.
 */
export const keysToRevoke = (
  classData: ClassData,
  releasedGrades: Record<string, Grade | undefined>,
): string[] => {
  const { restricted } = splitReleasedGrades(classData, releasedGrades);
  const out = new Set(restricted.map(r => r.key));
  const releasedKeys = new Set(Object.keys(releasedGrades || {}));
  for (const key of Object.keys(classData.grades || {})) {
    if (!releasedKeys.has(key)) out.add(key);
  }
  return [...out];
};
